import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { AdvancedFilter } from '../../../../../interfaces/interfaces';
import { BoothsComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { getFilterCategories } from '../../../../../services/services';
import { VyFilterSelect } from '../../../../common/VyFilterSelect';
import VyMultiSelect from '../../../../common/VyMultiSelect';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';

interface BoothsSettingsTableInterface {
  component: BoothsComponentInterface;
  onChange: (c: BoothsComponentInterface) => void;
}

const BoothsSettingsTable = ({ component, onChange }: BoothsSettingsTableInterface) => {
  const [filters, setFilters] = useState<AdvancedFilter[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<string[]>(
    component.advancedFilters?.filter((af) => af.datetime_deleted === undefined).map((af) => af._id) || []
  );
  const [excludeFilters, setExcludeFilters] = useState<string[]>(
    component.excludeFilters?.filter((ef) => ef.datetime_deleted === undefined).map((ef) => ef._id) || []
  );
  const { t } = useTranslation('pagebuilder');
  const event = useSelector((state) => state.common.event);
  const booths = useSelector((state) =>
    state.common.booths.filter((booth) => component?.showOnlyWithMeeting === false || booth.conferencerooms?.length > 0 || booth.meetingrooms?.length > 0)
  );

  useEffect(() => {
    setAdvancedFilters(component.advancedFilters?.filter((af) => af.datetime_deleted === undefined).map((af) => af._id) || []);
    setExcludeFilters(component.excludeFilters?.filter((ef) => ef.datetime_deleted === undefined).map((ef) => ef._id) || []);
  }, [component]);

  /**
   *
   * Filter handling
   */

  useEffect(() => {
    fetchFilters();
  }, [event]);

  async function fetchFilters() {
    if (event) {
      const { data: fdata } = await getFilterCategories(event._id);
      setFilters(fdata);
    }
  }

  const onPreselectedFilterChange = (value: string) => {
    const _preselectedFilter = filters
      .map((f) => f.categoryFilterValues)
      .flat()
      .find((cfv) => cfv._id === value);

    const _component = {
      ...component,
      preselectedFilter: component.preselectedFilter?._id === value ? undefined : _preselectedFilter,
    };
    onChange(_component);
  };

  const onExcludeFiltersChange = (values: string[]) => {
    const categoryFilterValues = filters.map((f) => f.categoryFilterValues).flat();
    const _excludeFilters = values.map((v) => categoryFilterValues.find((cfv) => cfv._id === v));
    // reason : _excludeFilters type is (CategoryFilterValue | undefined)[] because of the .find function
    // it will never be undefined since all the options are from the same state
    //@ts-ignore
    onChange({ ...component, excludeFilters: _excludeFilters });
  };

  const onAdvancedFiltersChange = (values: string[]) => {
    const categoryFilterValues = filters.map((f) => f.categoryFilterValues).flat();
    const _advancedFilters = values.map((v) => categoryFilterValues.find((cfv) => cfv._id === v));
    // reason : _advancedFilters type is (CategoryFilterValue | undefined)[] because of the .find function
    // it will never be undefined since all the options are from the same state
    //@ts-ignore
    onChange({ ...component, advancedFilters: _advancedFilters });
  };

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('pagebuilder:component-settings'),
      settings: [
        {
          label: t('pagebuilder:show-search'),
          type: 'boolean',
          path: 'showSearch',
        },
        {
          label: t('pagebuilder:show-filter'),
          type: 'boolean',
          path: 'showFilter',
        },
        {
          type: 'text',
          visible: component.showFilter,
          label: t('pagebuilder:filter-description'),
          path: 'filterDescription',
        },
        {
          label: t('pagebuilder:show-is-online-checkbox'),
          type: 'boolean',
          path: 'showIsOnlineCheckbox',
        },
      ],
    },
    {
      name: t('pagebuilder:booth-selection'),
      settings: [
        {
          label: t('pagebuilder:show-only-with-meetingrooms'),
          type: 'boolean',
          path: 'showOnlyWithMeeting',
        },
        {
          label: t('pagebuilder:show-randomly-sort'),
          type: 'boolean',
          path: 'useRandomSort',
        },
        {
          visible: filters.length > 0,
          label: t('pagebuilder:preselected-filter'),
          content: (
            <VyFilterSelect
              filters={filters}
              selectedValues={
                component.preselectedFilter?._id && component.preselectedFilter.datetime_deleted === undefined ? [component.preselectedFilter._id] : []
              }
              multiple={false}
              excludedValues={excludeFilters}
              onChange={(values) => {
                onPreselectedFilterChange((values as unknown) as string);
              }}
            />
          ),
        },
        {
          visible: filters.length > 0,
          label: t('booths-include-filters'),
          content: <VyFilterSelect filters={filters} selectedValues={advancedFilters} excludedValues={excludeFilters} onChange={onAdvancedFiltersChange} />,
        },
        {
          visible: filters.length > 0,
          label: t('booths-exclude-filters'),
          content: <VyFilterSelect filters={filters} selectedValues={excludeFilters} excludedValues={advancedFilters} onChange={onExcludeFiltersChange} />,
        },
        {
          label: t('booths-spotlight'),
          content: (
            <VyMultiSelect
              variant="outlined"
              name={`booths-spotlight-${component._id}`}
              onValueChange={(values) => {
                const spotlightBooths = booths.filter((b) => values.includes(b._id)).map((b) => b._id);
                onChange({ ...component, spotlightBooths });
              }}
              options={booths.map((b) => ({ value: b._id, name: b.name || '' }))}
              selectedOptions={component.spotlightBooths?.map((sb) => ({ value: sb, name: booths.find((b) => b._id === sb)?.name || '' })) || []}
            />
          ),
        },
      ],
    },
    {
      name: 'Boothanzeige',
      settings: [
        {
          label: t('pagebuilder:highlight-online'),
          path: 'highlightOnline',
          type: 'boolean',
        },
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default BoothsSettingsTable;
